// frontend/src/components/ProductCard.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Card, CardContent, CardMedia, Typography, Box, Button, Chip, TextField,
    IconButton, InputAdornment, ToggleButtonGroup, ToggleButton, Stack, Divider,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StyleIcon from '@mui/icons-material/Style';
import { motion } from 'framer-motion';

const showPrice = process.env.REACT_APP_PRICE === 'true';

// --- InCartControls edited to round weight display ---
const InCartControls = React.memo(function InCartControls({ orderItem, onUpdateCart, product, compact }) {
    const handleUpdate = useCallback((increment) => {
        const newQuantity = orderItem.quantity + increment;
        onUpdateCart(product, newQuantity, orderItem.mode);
    }, [orderItem, onUpdateCart, product]);

    // Remove text in brackets from mode for display
    const simpleMode = orderItem.mode.split('(')[0].trim();
    const capitalizedDisplayMode = simpleMode.charAt(0).toUpperCase() + simpleMode.slice(1);
    
    // Check if the mode is weight-related to format the quantity display
    const isOrderItemWeightMode = ['weight', 'lb', 'pound'].includes(simpleMode.toLowerCase());
    const displayQuantity = isOrderItemWeightMode 
        ? parseFloat(orderItem.quantity).toFixed(2) 
        : orderItem.quantity;

    return (
        <Box sx={{ p: compact ? 1 : 2, pt: 1, borderTop: '1px solid #eee' }}>
            <Typography variant="caption" display="block" textAlign="center" sx={{ mb: 1, color: 'text.secondary', fontSize: compact ? '0.65rem' : '0.75rem' }}>
                {compact ? capitalizedDisplayMode : `In Cart as: ${capitalizedDisplayMode}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <IconButton size="small" onClick={() => handleUpdate(-1)} disabled={orderItem.quantity <= 0} sx={{ p: compact ? 0.5 : 1 }}>
                    <RemoveIcon fontSize={compact ? "small" : "medium"} />
                </IconButton>
                <Typography sx={{ mx: compact ? 1 : 2, fontWeight: 'bold', fontSize: compact ? '0.85rem' : '1rem' }}>
                    {displayQuantity}
                </Typography>
                <IconButton size="small" onClick={() => handleUpdate(1)} sx={{ p: compact ? 0.5 : 1 }}>
                    <AddIcon fontSize={compact ? "small" : "medium"} />
                </IconButton>
            </Box>
        </Box>
    );
});


function ProductCard({ product, orderItemsForProduct = [], onUpdateCart, onGuestAction, isLoggedIn, compact = false }) {
    const isInOrder = orderItemsForProduct && orderItemsForProduct.length > 0;
    const availableModes = useMemo(() => Object.keys(product.modes || {}), [product]);

    const [selectedMode, setSelectedMode] = useState(availableModes[0] || '');
    const [quantity, setQuantity] = useState('1');
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkIsMobile = () => {
        const mobileBreakpoint = 900; // Adjusted breakpoint for better tablet handling
        setIsMobile(window.innerWidth < mobileBreakpoint);
        };
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => {
        window.removeEventListener('resize', checkIsMobile);
        };
    }, []);
    // When product changes, reset selectedMode to a valid one:
    useEffect(() => {
        const modes = Object.keys(product.modes || {});
        setSelectedMode(modes[0] || '');
        setQuantity('1');
    }, [product.id]); // only depend on product id

    const displayedPrice = useMemo(() => product.modes[selectedMode]?.price || 0, [product.modes, selectedMode]);

    const handleModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setSelectedMode(newMode);
            setQuantity('1'); // Reset quantity when mode changes
        }
    }, []);

    // Check if the selected mode is weight-related
    const isWeightMode = useMemo(() => ['weight', 'lb', 'pound'].includes((selectedMode || '').toLowerCase().split('(')[0].trim()), [selectedMode]);

    // Check if the selected mode is case/bag-related
    const isCaseOrBagMode = useMemo(() => {
        const modeStr = selectedMode || '';
        const modeLower = modeStr.toLowerCase();
        
        // If mode contains "case" and "*", force whole numbers by excluding it from decimal logic
        if (modeLower.includes('case') && modeStr.includes('*')) {
            return false;
        }

        if (modeLower.includes('piece')) {
            return false;
        }
        
        return modeLower.includes('case') || modeLower.includes('bag');
    }, [selectedMode]);

    const handleQuantityChange = useCallback((e) => {
        const value = e.target.value;
        // Allow decimals only for weight-like modes or case/bag modes, otherwise integers only.
        const regex = (isWeightMode || isCaseOrBagMode) ? /^\d*\.?\d*$/ : /^\d*$/;
        if (value === '' || regex.test(value)) {
            setQuantity(value);
        }
    }, [isWeightMode, isCaseOrBagMode]);

    const handleAddToCart = useCallback(() => {
        if (!isLoggedIn) {
            onGuestAction();
            return;
        }
        let numericQuantity = parseFloat(quantity);
        if (numericQuantity > 0 && selectedMode) {
            // Round to 2 decimal places for weight modes before adding to cart
            if (isWeightMode) {
                numericQuantity = Math.round(numericQuantity * 100) / 100;
            }
            // Round to nearest 0.25 for case/bag modes before adding to cart
            if (isCaseOrBagMode) {
                numericQuantity = Math.round(numericQuantity * 4) / 4;
            }
            onUpdateCart(product, numericQuantity, selectedMode);
        }
    }, [isLoggedIn, onGuestAction, quantity, selectedMode, isWeightMode, isCaseOrBagMode, onUpdateCart, product]);

    // --- EDIT: Logic for input bar unit display (removes parenthesis content) ---
    const displayModeForUnit = selectedMode ? selectedMode.split('(')[0].trim() : '';
    const modeUnit = isWeightMode
        ? 'lb'
        : displayModeForUnit
            ? `${displayModeForUnit.charAt(0).toUpperCase() + displayModeForUnit.slice(1)}`
            : '';

    // Logic to determine the correct unit for the price display
    const priceDisplayMode = selectedMode ? selectedMode.split('(')[0].trim() : '';
    const priceUnitDisplay = isWeightMode ? 'pound' : priceDisplayMode;

    return (
        <motion.div whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}>
            <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column', 
                maxWidth: compact ? { xs: 160, sm: 260 } : { xs: 215, sm: 260 }, // Significantly reduced width if compact
                borderRadius: 3, boxShadow: 3,
                border: '2px solid', borderColor: isInOrder ? 'success.main' : 'transparent',
                transition: 'border-color 0.3s ease-in-out', overflow: 'hidden',
                minHeight: compact ? 220 : 320 // Reduced height if compact to maintain aspect ratio feel
            }}>
                <Box sx={{ position: 'relative' }}>
                    <CardMedia
                        component="img" 
                        height={compact ? "90" : "140"} // Smaller image
                        image={product.image_url || 'https://via.placeholder.com/300x180.png?text=No+Image'}
                        alt={product.name} sx={{ objectFit: 'contain' }}
                    />
                    {isInOrder && (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: compact ? '1rem' : undefined }} />} 
                            label={compact ? "In Cart" : "In Cart"} 
                            color="success"
                            size="small" 
                            sx={{ 
                                position: 'absolute', top: 8, right: 8, fontWeight: 'bold',
                                transform: compact ? 'scale(0.8)' : 'none', transformOrigin: 'top right'
                            }}
                        />
                    )}
                </Box>
                <CardContent sx={{ flexGrow: 1, p: compact ? 1 : 2, pb: 0 }}>
                    <Typography gutterBottom variant={compact ? "subtitle2" : "h6"} component="h2" noWrap sx={{ fontWeight: 600, fontSize: compact ? '0.9rem' : undefined }}>
                        {product.name}
                    </Typography>
                    
                    {showPrice && (
                        <Typography variant={compact ? "body2" : "h6"} color="primary.main" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                            {`$${displayedPrice.toFixed(2)} CAD / ${priceUnitDisplay}`}
                        </Typography>
                    )}
{/* <Typography color="text.secondary" variant="body2" sx={{ minHeight: '40px' }}>
                        {product.description || "No description available."}
                    </Typography> */}
                </CardContent>

                <Box sx={{ mt: 'auto' }}>
                    {isInOrder && (
                        <Box>
                            <Divider sx={{ mx: compact ? 1 : 2, mb: 0 }} />
                            {orderItemsForProduct.map((item) => (
                                <InCartControls
                                    key={item.mode}
                                    orderItem={item}
                                    onUpdateCart={onUpdateCart}
                                    product={product}
                                    compact={compact}
                                />
                            ))}
                        </Box>
                    )}
                    
                    <Box sx={{ p: compact ? 1 : 2, pt: 1, mt: 'auto', display: 'flex', flexDirection: 'column', gap: compact ? 1 : 2 }}>
                        <ToggleButtonGroup
                            color="primary" value={selectedMode} exclusive
                            onChange={handleModeChange} aria-label="Order mode" fullWidth size="small"
                        >
                            {availableModes.map((mode) => {
                                // --- EDIT: Logic to split mode with parentheses onto two lines ---
                                const openParenIndex = mode.indexOf('(');
                                let mainText, subText;

                                if (openParenIndex > -1 && mode.endsWith(')')) {
                                    mainText = mode.substring(0, openParenIndex).trim();
                                    subText = mode.substring(openParenIndex).trim();
                                } else {
                                    mainText = mode;
                                    subText = null;
                                }
                                const capitalizedMainText = mainText.charAt(0).toUpperCase() + mainText.slice(1);

                                return (
                                    <ToggleButton
                                        key={mode} value={mode}
                                        sx={{ 
                                            flexGrow: 1, 
                                            textTransform: 'none', 
                                            '&.Mui-selected': { backgroundColor: '#75a84b', color: '#fff' }, 
                                            '&.Mui-selected:hover': { backgroundColor: '#7c8c58' },
                                            lineHeight: 1.2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            py: compact ? 0.2 : undefined,
                                            px: compact ? 0.5 : undefined,
                                        }}
                                    >
                                        {/* {!isMobile && !compact ? ( <StyleIcon sx={{ mr: 1, fontSize: '1rem', flexShrink: 0 }} />) : null} */}
                                        {subText ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                                                <Typography component="span" sx={{ fontWeight: 'inherit', lineHeight: 1.2, fontSize: compact ? '0.75rem' : '0.875rem' }}>
                                                    {capitalizedMainText}
                                                </Typography>
                                                <Typography component="span" sx={{ fontSize: compact ? '0.6rem' : '0.7rem', lineHeight: 1, textTransform: 'lowercase', opacity: 0.9 }}>
                                                    {subText}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography component="span" sx={{ fontSize: compact ? '0.75rem' : undefined }}>
                                                {capitalizedMainText}
                                            </Typography>
                                        )}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>

                        <Stack direction="row" spacing={compact ? 0.5 : 1.5} alignItems="center">
                            <TextField
                                fullWidth variant="outlined" size="small" type="number"
                                value={quantity} onChange={handleQuantityChange} label={compact ? "" : "Quantity"}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: compact ? '0.7rem' : undefined } }}>{modeUnit}</InputAdornment>,
                                    inputProps: { 
                                        min: 0,
                                        step: isWeightMode ? '0.1' : (isCaseOrBagMode ? '0.25' : '1'),
                                        style: { padding: compact ? '6px 8px' : undefined, fontSize: compact ? '0.85rem' : undefined }
                                    }
                                }}
                                InputLabelProps={compact ? { shrink: true } : undefined}
                            />
                            <Button
                                variant="contained" onClick={handleAddToCart} 
                                disabled={!quantity || parseFloat(quantity) <= 0}
                                sx={{ 
                                    py: compact ? '4px' : '8px', 
                                    px: compact ? '8px' : '12px', 
                                    minWidth: compact ? '30px' : undefined,
                                    whiteSpace: 'nowrap', 
                                    backgroundColor: '#75a84b', 
                                    '&:hover': { backgroundColor: '#7c8c58' } 
                                }}
                            >
                                <AddShoppingCartIcon sx={{ color: '#fff', fontSize: compact ? '1.2rem' : undefined }} />
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Card>
        </motion.div>
    );
}

// Memoize the entire product card to avoid re-renders when not needed
export default React.memo(ProductCard, (prev, next) => {
    // quick shallow comparison:
    if (prev.product.id !== next.product.id) return false;
    if (prev.isLoggedIn !== next.isLoggedIn) return false;
    if (prev.compact !== next.compact) return false; // Added comparison for compact prop
    // compare orderItemsForProduct length and shallow contents
    const a = prev.orderItemsForProduct || [];
    const b = next.orderItemsForProduct || [];
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].quantity !== b[i].quantity || a[i].mode !== b[i].mode) return false;
    }
    // onUpdateCart/onGuestAction are expected to be stable (useCallback in parent)
    return true;
});